import type { FC, ReactNode } from 'react'
import type { MotionValue } from 'motion/react'

import { useRef } from 'react'
import { motion, useScroll, useTransform } from 'motion/react'
import { Star } from 'lucide-react'
import { useReduceMotion } from '@/hooks/use-reduce-motion'
import Marquee from '@/components/landing/marquee'
import { testimonials } from '@/constants/content'

interface WordProps {
    children: string
    progress: MotionValue<number>
    range: [number, number]
    still: boolean
}

const words = testimonials.featured.quote.split(' ')

// Each word brightens in turn as the quote moves up the screen, so it reads like it is being spoken.
const Word: FC<WordProps> = ({ children, progress, range, still }): ReactNode => {
    const opacity = useTransform(progress, range, [0.24, 1])

    return (
        <motion.span style={still ? undefined : { opacity }}>
            {children}
        </motion.span>
    )
}

const QuoteMark: FC<{ className?: string }> = ({ className }): ReactNode => (
    <svg aria-hidden='true' viewBox='0 0 96 72' className={className}>
        <path
            d='M0 72 V44 C0 18 12 4 38 0 L41 12 C27 16 21 24 20 36 H38 V72 Z M55 72 V44 C55 18 67 4 93 0 L96 12 C82 16 76 24 75 36 H93 V72 Z'
            fill='currentColor'
        />
    </svg>
)

const Testimonials: FC = (): ReactNode => {
    const quoteRef = useRef<HTMLQuoteElement>(null)
    const sectionRef = useRef<HTMLElement>(null)
    const reduce = useReduceMotion()

    const { scrollYProgress } = useScroll({ target: quoteRef, offset: ['start 85%', 'end 62%'] })
    const { scrollYProgress: sectionProgress } = useScroll({ target: sectionRef, offset: ['start end', 'end start'] })
    const markRotate = useTransform(sectionProgress, [0, 1], [-18, 14])
    const markScale = useTransform(sectionProgress, [0, 0.4, 1], [0.6, 1, 1.1])

    return (
        <section
            ref={sectionRef}
            id='testimonials'
            aria-label='What clients say'
            className='relative overflow-x-clip bg-blaze text-ink [--focus-ring:var(--ink)]'
        >
            <div className='shell py-24 sm:py-32 lg:py-40'>
                <figure className='relative max-w-[70rem]'>
                    <motion.div style={reduce ? undefined : { rotate: markRotate, scale: markScale }} className='w-fit origin-bottom-left'>
                        <QuoteMark className='h-14 w-auto sm:h-20 lg:h-24' />
                    </motion.div>

                    <blockquote
                        ref={quoteRef}
                        className='mt-8 font-heading text-[1.7rem] font-bold leading-[1.12] tracking-[-0.02em] sm:mt-10 sm:text-4xl lg:text-[3.35rem] lg:leading-[1.08]'
                    >
                        <p>
                            {words.map((word, index) => {
                                const start = (index / words.length) * 0.82

                                return (
                                    <span key={`${word}-${index}`}>
                                        <Word progress={scrollYProgress} range={[start, start + 0.18]} still={reduce}>
                                            {word}
                                        </Word>

                                        {index < words.length - 1 ? ' ' : ''}
                                    </span>
                                )
                            })}
                        </p>
                    </blockquote>

                    <figcaption className='mt-10 flex flex-wrap items-center gap-4'>
                        <span aria-hidden='true' className='h-[3px] w-12 bg-ink' />

                        <span className='eyebrow text-[0.875rem] sm:text-[0.9375rem]'>
                            {testimonials.featured.author}
                        </span>

                        <span aria-hidden='true' className='flex gap-0.5'>
                            {[0, 1, 2, 3, 4].map(index => (
                                <Star key={index} className='size-4 fill-ink text-ink' />
                            ))}
                        </span>
                    </figcaption>
                </figure>
            </div>

            <div className='border-t-2 border-ink pb-16 pt-10 sm:pb-20 sm:pt-12'>
                <Marquee
                    label='client quotes'
                    speed={1.6}
                    reactToScroll={false}
                    controlClassName='right-4 -top-7 translate-y-0 bg-blaze text-ink sm:right-8 lg:right-12'
                    trackClassName='py-2'
                >
                    <ul className='flex items-stretch gap-5 pl-5 sm:gap-6 sm:pl-6'>
                        {testimonials.row.map(item => (
                            <li
                                key={item.quote}
                                className='flex w-[19rem] shrink-0 flex-col justify-between gap-6 rounded-lg border-2 border-ink bg-paper p-6 shadow-[5px_5px_0_0_var(--color-ink)] sm:w-[24rem] sm:p-7'
                            >
                                <p className='text-lg font-medium leading-snug sm:text-xl'>
                                    {`“${item.quote}”`}
                                </p>

                                {item.author && (
                                    <p className='eyebrow text-ink/70'>
                                        {item.author}
                                    </p>
                                )}
                            </li>
                        ))}
                    </ul>
                </Marquee>
            </div>
        </section>
    )
}

export default Testimonials
